import { useQuery } from "@tanstack/react-query";
import { getStatusPropertyFields } from "../../api/property-stage";
import { FieldLevel, TrackStatusFormField } from "../../dtos/property-stage";
import {
  generateDefaultValues,
  generateZodSchema,
} from "../../utils/track-stages";
import { FormSection, Track } from "../../types";
import { useParams } from "react-router-dom";
import { useTrackQuery } from "../track.hook";

interface StatusPropertyFieldProps {
  statusId?: number;
}

const useStatusPropertyFields = ({ statusId }: StatusPropertyFieldProps) => {
  const { trackId } = useParams<{ trackId: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ["status-property-fields", statusId],
    queryFn: async () => getStatusPropertyFields(statusId as number),
    enabled: !!statusId,
  });

  const { data: track } = useTrackQuery({
    uid: trackId,
  });

  const formStructure: FormSection[] = data?.data
    ? Object.values(FieldLevel)
      .filter(
        (fieldLevel) =>
          fieldLevel !== FieldLevel.Document &&
          fieldLevel !== FieldLevel.Amenity,
      )
      .map((fieldLevel) => ({
        fieldLevel,
        fields: data.data.filter(
          (field: any) => field.fieldLevel === fieldLevel
        ) as TrackStatusFormField[],
      }))
      .filter((section) => section.fields.length > 0)
    : [];

  const docsRequired = data?.data.filter(
    (field: any) => field.fieldLevel === FieldLevel.Document,
  );

  const amenitiesRequired = data?.data.filter(
    (field: any) => field.fieldLevel === FieldLevel.Amenity,
  );

  const defaultValues = generateDefaultValues({
    sections: formStructure,
    track: track as Track,
    amenitiesRequired: amenitiesRequired?.length > 0,
    docsRequired: docsRequired?.length > 0,
  });

  const schema =
    formStructure.length > 0 ||
      docsRequired?.length > 0 ||
      amenitiesRequired?.length > 0
      ? generateZodSchema({
        sections: formStructure,
        amenitiesRequired: amenitiesRequired?.length > 0,
        docsRequired: docsRequired?.length > 0,
      })
      : undefined;

  return {
    fields: data?.data,
    isLoading,
    error,
    schema,
    formStructure,
    docsRequired: docsRequired?.length > 0,
    amenitiesRequired: amenitiesRequired?.length > 0,
    defaultValues,
  };
};

export default useStatusPropertyFields;
